import styled from '@emotion/styled'
import { Skeleton } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'

import {
  AppointmentType,
  ReservationPaymentType,
  ReserveResult,
  SupportedLanguage,
} from '../../../__generated__/api'
import { MinMaxDiv } from '../../../common/components/Layout/Layout'
import UserSelect from '../../../common/components/UserSelect/UserSelect'
import { useApi } from '../../../common/hooks/useApi'
import { useInsurancePayerOption } from '../../../common/hooks/useInsurance'
import { useOHC } from '../../../common/hooks/useOHC'
import i18n from '../../../common/i18n/i18n'
import api from '../../../common/services/api'
import { scale } from '../../../common/utils/scale'
import {
  isFromAppAtom,
  isRedirectedFromInsuranceProviderAtom,
  selectedAppointmentIdAtom,
  selectedAppointmentLengthAtom,
  selectedAppointmentNodeConnectionIdAtom,
  selectedAppointmentNodeIdAtom,
  selectedAppointmentServiceIdAtom,
  selectedAppointmentTypeAtom,
} from '../../../state/common/atoms'
import { initialReserveHistoryRestoredAtom } from '../../../state/reserve/atoms'
import { isPaidByOHC } from '../../payerSelection/utils'
import InsuranceAdditionalDetails from '../components/InsuranceAdditionalDetails'
import PaymentTypeSelect from '../components/PaymentTypeSelect'
import ReserveForm from '../components/ReserveForm'
import { useFreedomOfChoice } from '../hooks/useFreedomOfChoice'
import useReserveHistory from '../hooks/useReserveHistory'
import useReserveState, { ReserveStateProvider } from '../hooks/useReserveState'

const Container = styled.div`
  min-height: 500px;
  padding-bottom: ${scale(12)};
`

const Skeletons = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${scale(2)};
  gap: ${scale(1.5)};
`

const ReserveView: React.FC<React.PropsWithChildren<Props>> = ({
  onBack,
  onReserved,
  onBackToStart,
}) => {
  const {
    appointmentId,
    loginStatus,
    loggedInUser,
    selectedUser,
    setSelectedUser,
    userPending,
    node,
    nodePending,
    selectedInsuranceContractId,
    setSelectedInsuranceContractId,
    selectedInsurancePayerId,
    setSelectedInsurancePayerId,
    setInsuranceAdditionalDetails,
    setPaymentType,
    paymentTypeOptions,
    selectedAppointmentType,
    isDentalAppointment,
    setShouldCheckCallbackAllowed,
  } = useReserveState()

  const { isOHCSide } = useOHC()
  const isCallbackAppointment = selectedAppointmentType === AppointmentType.Callback
  const selectedAppointmentNodeId = useRecoilValue(selectedAppointmentNodeIdAtom)
  const isRedirectedFromInsuranceProvider = useRecoilValue(isRedirectedFromInsuranceProviderAtom)
  const selectedNodeConnectionId = useRecoilValue(selectedAppointmentNodeConnectionIdAtom)
  const selectedServiceId = useRecoilValue(selectedAppointmentServiceIdAtom)
  const { data: appointmentInfo } = useApi(
    api.v1.appointmentInfo,
    {
      appointmentId: String(appointmentId),
      lang: i18n.language as SupportedLanguage,
      ...(isOHCSide && {
        nodeId: selectedAppointmentNodeId,
        connectionId: selectedNodeConnectionId,
        isOHC: true,
      }),
    },
    null,
    Boolean(appointmentId)
  )

  const isFromApp = useRecoilValue(isFromAppAtom)

  const [shouldDisplayUserSelect, setShouldDisplayUserSelect] = useState<boolean>(!isFromApp)
  const [shouldDisplayPaymentTypeSelect, setShouldDisplayPaymentTypeSelect] =
    useState<boolean>(true)

  const freedomOfChoiceModal = useFreedomOfChoice({
    selectedUser,
    selectedNode: node,
    selectedInsuranceContractId,
    isOHCSide,
    selectedNodeConnectionId,
    selectedServiceId,
    onBackToStart,
    setShouldDisplayPaymentTypeSelect,
  })

  const { insurancePayerOption } = useInsurancePayerOption(
    selectedInsuranceContractId,
    selectedInsurancePayerId
  )
  const [showAdditionalInsuranceDetails, setShowAdditionalInsuranceDetails] =
    useState<boolean>(true)
  const isImpersonated = loginStatus === 'impersonated'
  // Set payment type to occupational for OHC side
  useEffect(() => {
    if (isOHCSide) {
      if (
        appointmentInfo &&
        ((selectedAppointmentType === AppointmentType.Clinic &&
          appointmentInfo.ohcClinicAppointmentRule &&
          isPaidByOHC(appointmentInfo.ohcClinicAppointmentRule, isImpersonated)) ||
          (selectedAppointmentType === AppointmentType.Video &&
            appointmentInfo.ohcVideoAppointmentRule &&
            isPaidByOHC(appointmentInfo.ohcVideoAppointmentRule, isImpersonated)) ||
          (selectedAppointmentType === AppointmentType.Phone &&
            appointmentInfo.ohcPhoneAppointmentRule &&
            isPaidByOHC(appointmentInfo.ohcPhoneAppointmentRule, isImpersonated)))
      ) {
        setPaymentType(ReservationPaymentType.Occupational)
      } else {
        // Selected payment type is not covered by OHC
        setPaymentType(ReservationPaymentType.Self)
      }
    }
  }, [isImpersonated, appointmentInfo, isOHCSide, selectedAppointmentType, setPaymentType])

  useEffect(() => {
    if (isFromApp || isOHCSide) {
      setShouldCheckCallbackAllowed(true)
    }
  }, [isFromApp, isOHCSide, setShouldCheckCallbackAllowed])

  if (!selectedUser || (userPending && Boolean(loginStatus))) {
    return (
      <MinMaxDiv size="620px">
        <Skeletons>
          <Skeleton variant="rectangular" height="56px" />
          <Skeleton variant="rectangular" height="71px" />
          <Skeleton variant="rectangular" height="136px" />
          <Skeleton variant="rectangular" height="56px" />
        </Skeletons>
      </MinMaxDiv>
    )
  }

  if (!isRedirectedFromInsuranceProvider) {
    if (
      loginStatus === 'authenticated' &&
      loggedInUser &&
      loggedInUser.isDelegateUser &&
      !isOHCSide &&
      shouldDisplayUserSelect
    ) {
      return (
        <UserSelect
          onSelectUser={(user) => {
            setSelectedUser(user)
            setShouldDisplayUserSelect(false)
            setShouldCheckCallbackAllowed(true)
          }}
          onBack={onBack}
          onReserveSelf={() => {
            setSelectedUser(loggedInUser)
            setShouldDisplayUserSelect(false)
            setShouldCheckCallbackAllowed(true)
          }}
          appointmentInfo={appointmentInfo}
          node={node}
          nodePending={nodePending}
          disableUndefinedSelection={Boolean(selectedInsuranceContractId) || isCallbackAppointment}
        />
      )
    }

    if (
      !freedomOfChoiceModal &&
      !selectedInsuranceContractId &&
      !isOHCSide &&
      shouldDisplayPaymentTypeSelect
    ) {
      return (
        <PaymentTypeSelect
          isDentalAppointment={isDentalAppointment}
          options={paymentTypeOptions}
          onSelect={(paymentType) => {
            setShouldDisplayPaymentTypeSelect(false)
            setPaymentType(paymentType)
          }}
          onBack={onBack}
          onBackToStart={onBackToStart}
        />
      )
    }
  }

  if (freedomOfChoiceModal) {
    return freedomOfChoiceModal
  }

  if (
    insurancePayerOption?.additionalInfoForm &&
    showAdditionalInsuranceDetails &&
    !isDentalAppointment
  ) {
    return (
      <InsuranceAdditionalDetails
        additionalInfoForm={insurancePayerOption.additionalInfoForm}
        onSubmitDetails={(details) => {
          setInsuranceAdditionalDetails(details)
          setShowAdditionalInsuranceDetails(false)
        }}
        onBack={onBack}
        onContinueAsSelfPaying={() => {
          setSelectedInsuranceContractId(null)
          setSelectedInsurancePayerId(null)
          setPaymentType(ReservationPaymentType.Self)
          setShowAdditionalInsuranceDetails(false)
        }}
        onContinueAsSelfPayingVerified={() => {
          if (insurancePayerOption.fallbackPaymentOption) {
            setSelectedInsurancePayerId(insurancePayerOption.fallbackPaymentOption)
          } else {
            setSelectedInsurancePayerId(null)
            setSelectedInsuranceContractId(null)
            setPaymentType(ReservationPaymentType.Self)
          }
          setShowAdditionalInsuranceDetails(false)
        }}
      />
    )
  }

  return <ReserveForm onReserved={onReserved} onBack={onBack} />
}

interface Props {
  onBack(): void
  onBackToStart(): void
  onReserved(reserveResult: ReserveResult): void
}

const ReserveViewWrapper: React.FC<React.PropsWithChildren<Props>> = ({
  onReserved,
  onBack,
  onBackToStart,
}) => {
  useReserveHistory()

  const { appointmentId: appointmentIdFromParams } = useParams<{ appointmentId?: string }>()
  const [appointmentId, setAppointmentId] = useRecoilState(selectedAppointmentIdAtom)
  const [appointmentType, setAppointmentType] = useRecoilState(selectedAppointmentTypeAtom)
  const appointmentLength = useRecoilValue(selectedAppointmentLengthAtom)

  // Used in useLockAppointment during the cleanup phase. This is needed to prevent unlocking the appointment if the user has already reserved it.
  // This needs to be ref because components are not rerendered when onReserved callback is called causing cleanup to have stale state.
  const isReservedRef = useRef(false)

  useEffect(() => {
    if (!appointmentId && appointmentIdFromParams && parseInt(appointmentIdFromParams, 10)) {
      setAppointmentId(parseInt(appointmentIdFromParams, 10))
    }
  }, [appointmentId, appointmentIdFromParams, setAppointmentId])

  const initialReserveHistoryRestored = useRecoilValue(initialReserveHistoryRestoredAtom)

  if (!appointmentId || !initialReserveHistoryRestored) {
    return null
  }

  if (!appointmentType) {
    // default to clinic
    setAppointmentType(AppointmentType.Clinic)
  }

  if (!appointmentLength) {
    throw new Error('appointmentLength is not set')
  }

  const onReserve = (reserveResult: ReserveResult) => {
    isReservedRef.current = true
    onReserved(reserveResult)
  }

  return (
    <ReserveStateProvider
      appointmentId={appointmentId}
      appointmentType={appointmentType || AppointmentType.Clinic}
      appointmentLength={appointmentLength}
      isReserved={isReservedRef}
    >
      <Container>
        <ReserveView onReserved={onReserve} onBack={onBack} onBackToStart={onBackToStart} />
      </Container>
    </ReserveStateProvider>
  )
}

export default ReserveViewWrapper
